import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Topbar } from "../components/shell/Topbar";
import { useToast } from "../components/shell/Toast";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { cn } from "@/packages/utils/cn";

interface Webhook {
  id: string;
  referenceId: string;
  projectId: string;
  name: string;
  description: string | null;
  forwardEnabled: boolean;
  forwardUrl: string | null;
}

interface CapturedRequest {
  id: string;
  method: string;
  headers: Record<string, string>;
  body: unknown;
  query: Record<string, string>;
  ip: string;
  timestamp: string;
  forwardResponse?: {
    status: number;
    durationMs: number;
    error?: string;
  };
  replays?: Array<{
    at: string;
    status: number;
    durationMs: number;
    by: string | null;
  }>;
}

export function WebhooksPage() {
  const { teamId, projectId } = useParams();
  const { teams, projects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find((p) => p.referenceId === projectId);

  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [requests, setRequests] = useState<CapturedRequest[]>([]);
  const [creating, setCreating] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const { toast } = useToast();

  const refresh = async () => {
    if (!projectId) return;
    try {
      const d: Webhook[] = await fetch(
        `/api/projects/${projectId}/webhooks`,
      ).then((r) => r.json());
      setWebhooks(d);
      if (!selectedId && d[0]) setSelectedId(d[0].id);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!selectedId) return;
    fetch(`/api/projects/${projectId}/webhooks/${selectedId}/requests`)
      .then((r) => r.json())
      .then(setRequests);

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(
      `${proto}://${window.location.host}/ws/webhook/${selectedId}`,
    );
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data) as CapturedRequest;
        setRequests((prev) => {
          const idx = prev.findIndex((r) => r.id === data.id);
          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = data;
            return next;
          }
          return [data, ...prev].slice(0, 50);
        });
      } catch {}
    };
    return () => {
      ws.close();
    };
  }, [selectedId, projectId]);

  const selectedWebhook = webhooks.find((w) => w.id === selectedId);

  if (!team || !project) return null;

  const baseHost =
    window.location.host.replace(/^[^.]+\./, "") || window.location.host;
  const inboxUrl = (refId: string) =>
    `${window.location.protocol}//${team.referenceId}-${project.slug}.${baseHost}/api/webhook/${refId}`;

  const updateWebhook = async (id: string, patch: Partial<Webhook>) => {
    let snapshot: Webhook | undefined;
    setWebhooks((curr) => {
      snapshot = curr.find((w) => w.id === id);
      return curr.map((w) => (w.id === id ? { ...w, ...patch } : w));
    });

    try {
      const res = await fetch(`/api/projects/${projectId}/webhooks/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
    } catch (err) {
      if (snapshot) {
        const snap = snapshot;
        setWebhooks((curr) => curr.map((w) => (w.id === id ? snap : w)));
      }
      toast({
        tone: "error",
        title: "Update failed",
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <>
      <Topbar
        title="Webhook inbox"
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          { label: project.name, to: `/teams/${team.id}/projects/${project.referenceId}` },
          { label: "Webhooks" },
        ]}
        actions={<Button size="sm" onClick={() => setCreating(true)}>+ New endpoint</Button>}
      />

      {loading ? (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[300px_1fr] overflow-hidden">
          <aside className="border-r border-rule bg-card-paper overflow-auto">
            <div className="px-4 py-3 border-b border-rule">
              <span className="text-eyebrow">Endpoints</span>
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="px-4 py-3 border-b border-rule-soft border-l-2 border-l-transparent space-y-1.5"
              >
                <Skeleton className="h-3.5 w-3/5" />
                <Skeleton className="h-2.5 w-4/5" />
                <Skeleton className="h-2.5 w-20" />
              </div>
            ))}
          </aside>
          <div className="overflow-auto">
            <div className="px-6 py-4 border-b border-rule bg-paper space-y-4">
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-8 w-full" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                <div className="space-y-1.5">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-8 w-full" />
                </div>
                <Skeleton className="h-8 w-12" />
              </div>
            </div>
            <div className="px-6 py-4 space-y-1.5">
              <Skeleton className="h-3 w-40 mb-3" />
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          </div>
        </div>
      ) : webhooks.length === 0 ? (
        <div className="px-4 sm:px-8 py-10 text-center text-sm text-muted-foreground">
          No webhook endpoints yet.
          <br />
          <Button className="mt-3" onClick={() => setCreating(true)}>
            Create your first webhook
          </Button>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[300px_1fr] overflow-hidden">
          {/* Webhooks list + selected meta */}
          <aside className="border-r border-rule bg-card-paper overflow-auto">
            <div className="px-4 py-3 border-b border-rule">
              <span className="text-eyebrow">Endpoints · {webhooks.length}</span>
            </div>
            {webhooks.map((w) => (
              <button
                key={w.id}
                onClick={() => setSelectedId(w.id)}
                className={cn(
                  "block w-full text-left px-4 py-3 border-b border-rule-soft border-l-2",
                  w.id === selectedId
                    ? "bg-panel border-l-signal-lavender"
                    : "border-l-transparent hover:bg-panel/40",
                )}
              >
                <div className="font-medium text-sm truncate">{w.name}</div>
                <div className="font-mono text-[10px] text-faint truncate mt-0.5">
                  /api/webhook/{w.referenceId.slice(0, 12)}…
                </div>
                <div className="font-mono text-[10px] text-signal-lavender mt-1">
                  {w.forwardEnabled ? "● bypass on" : "○ bypass off"}
                </div>
              </button>
            ))}
          </aside>

          {/* Right column: webhook config + requests */}
          <div className="overflow-auto">
            {selectedWebhook && (
              <>
                <div className="px-6 py-4 border-b border-rule bg-paper space-y-4">
                  <div>
                    <div className="text-eyebrow mb-1">Inbox URL</div>
                    <code className="font-mono text-xs text-foreground bg-panel border border-rule rounded-md px-2 py-1.5 block break-all">
                      {inboxUrl(selectedWebhook.referenceId)}
                    </code>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                    <div className="space-y-1.5">
                      <Label>Forward (bypass) URL</Label>
                      <Input
                        value={selectedWebhook.forwardUrl ?? ""}
                        onChange={(e) =>
                          setWebhooks((prev) =>
                            prev.map((w) =>
                              w.id === selectedWebhook.id
                                ? { ...w, forwardUrl: e.target.value }
                                : w,
                            ),
                          )
                        }
                        onBlur={(e) =>
                          updateWebhook(selectedWebhook.id, {
                            forwardUrl: e.target.value || null,
                          })
                        }
                        placeholder="https://localhost:3000/webhooks/sink"
                        className="font-mono text-xs"
                      />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Label>Bypass</Label>
                      <Switch
                        checked={selectedWebhook.forwardEnabled}
                        onCheckedChange={(v) =>
                          updateWebhook(selectedWebhook.id, {
                            forwardEnabled: v,
                          })
                        }
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    When bypass is on, every captured request is forwarded to
                    the URL above (preserving method, headers and body) and the
                    response is recorded.
                  </p>
                </div>

                {/* Requests list */}
                <div className="px-6 py-4">
                  <div className="text-eyebrow mb-3">
                    Captured requests · {requests.length}
                  </div>
                  {requests.length === 0 ? (
                    <div className="text-sm text-muted-foreground italic">
                      No requests yet. Send one to the inbox URL to see it here
                      in real time.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {requests.map((r) => (
                        <Link
                          key={r.id}
                          to={`/teams/${teamId}/projects/${projectId}/webhooks/${selectedId}?request=${r.id}`}
                          className="block bg-card-paper border border-rule rounded-md px-3 py-2 hover:bg-panel/40 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-[10px] font-bold text-signal-ok">
                              {r.method}
                            </span>
                            <span className="font-mono text-xs flex-1 truncate">
                              {r.headers["user-agent"]?.slice(0, 40) ?? r.ip}
                            </span>
                            {r.forwardResponse && (
                              <span
                                className={cn(
                                  "font-mono text-[10px] px-1.5 py-0.5 rounded",
                                  r.forwardResponse.status >= 200 &&
                                    r.forwardResponse.status < 300
                                    ? "bg-signal-ok-soft text-signal-ok"
                                    : "bg-signal-red-soft text-signal-red",
                                )}
                              >
                                → {r.forwardResponse.status}
                              </span>
                            )}
                            <span className="font-mono text-[10px] text-faint">
                              {new Date(r.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {creating && (
        <CreateWebhookDialog
          projectId={projectId!}
          onClose={() => setCreating(false)}
          onCreated={(w) => {
            setCreating(false);
            setSelectedId(w.id);
            refresh();
          }}
        />
      )}
    </>
  );
}

function CreateWebhookDialog({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: (w: Webhook) => void;
}) {
  const [name, setName] = useState("");
  const [forwardUrl, setForwardUrl] = useState("");
  const [forwardEnabled, setForwardEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/webhooks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          forwardUrl: forwardUrl || null,
          forwardEnabled,
        }),
      });
      if (!res.ok) {
        setError((await res.json()).error ?? "Failed");
        return;
      }
      onCreated(await res.json());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New webhook endpoint</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Stripe webhooks" />
          </div>
          <div className="space-y-1.5">
            <Label>Forward URL (optional)</Label>
            <Input
              value={forwardUrl}
              onChange={(e) => setForwardUrl(e.target.value)}
              placeholder="https://localhost:3000/webhooks"
              className="font-mono text-xs"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Bypass enabled</Label>
            <Switch checked={forwardEnabled} onCheckedChange={setForwardEnabled} />
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name || submitting}>
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" />
                Creating…
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
